import React, { useState, useRef } from 'react';
import { Send, Loader2, ChevronDown, Upload, X, Image, Type, Plus, FileText } from 'lucide-react';
import { useSubjects } from '../hooks/useSubjects';
import { LEVELS, getSubjectIcon } from '../lib/subjects';

// Legacy export so existing imports of SUBJECTS keep working. Components that
// need the full live catalog should call `useSubjects()` instead.
const SUBJECTS = [
    { id: 'business', name: 'Business' },
    { id: 'economics', name: 'Economics' },
    { id: 'geography', name: 'Geography' },
    { id: 'psychology', name: 'Psychology' },
    { id: 'history', name: 'History' },
    { id: 'sociology', name: 'Sociology' },
    { id: 'english_literature', name: 'English Literature' },
];

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const VALID_FILE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

function validateAndReadFile(file, onResult) {
    if (!VALID_FILE_TYPES.includes(file.type)) {
        alert('Please upload a JPEG, PNG, WebP image or PDF');
        return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
        alert('File must be less than 10MB');
        return;
    }
    const reader = new FileReader();
    reader.onload = (e) => onResult(e.target.result);
    reader.readAsDataURL(file);
}

// Single image field (for Question)
function SingleImageField({ label, textValue, onTextChange, image, onImageChange, placeholder, required, testIdPrefix }) {
    const [mode, setMode] = useState('text');
    const [preview, setPreview] = useState(null);
    const fileRef = useRef(null);

    const handleUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        validateAndReadFile(file, (result) => {
            onImageChange(result);
            setPreview(result);
        });
    };

    const removeImage = () => {
        onImageChange(null);
        setPreview(null);
        if (fileRef.current) fileRef.current.value = '';
    };

    return (
        <div>
            <label className="block text-sm font-semibold text-zinc-700 mb-2">{label}</label>
            <ModeToggle mode={mode} setMode={setMode} testIdPrefix={testIdPrefix} />
            {mode === 'text' ? (
                <textarea
                    value={textValue}
                    onChange={(e) => onTextChange(e.target.value)}
                    className="brutalist-textarea"
                    placeholder={placeholder}
                    required={required && !image}
                    data-testid={`${testIdPrefix}-input`}
                />
            ) : (
                <div className="space-y-3">
                    {preview ? (
                        <div className="relative inline-block">
                            {preview.startsWith('data:application/pdf') ? (
                                <div className="flex items-center gap-3 p-4 rounded-lg border-2 border-zinc-950 bg-zinc-50" data-testid={`${testIdPrefix}-image-preview`}>
                                    <FileText className="w-8 h-8 text-[#A78BFA]" />
                                    <span className="text-sm font-medium">PDF uploaded</span>
                                </div>
                            ) : (
                                <img src={preview} alt={`${label} preview`} className="max-h-48 object-contain rounded-lg border-2 border-zinc-950" data-testid={`${testIdPrefix}-image-preview`} />
                            )}
                            <button type="button" onClick={removeImage} className="absolute top-1 right-1 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600" data-testid={`${testIdPrefix}-remove-image`}>
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ) : (
                        <UploadZone fileRef={fileRef} onUpload={handleUpload} label={label} testIdPrefix={testIdPrefix} />
                    )}
                    <NotesField textValue={textValue} onTextChange={onTextChange} label={label} testIdPrefix={testIdPrefix} />
                </div>
            )}
        </div>
    );
}

// Multi-image field (for Answer & Mark Scheme)
function MultiImageField({ label, textValue, onTextChange, images, onImagesChange, placeholder, required, testIdPrefix, minHeight }) {
    const [mode, setMode] = useState('text');
    const fileRef = useRef(null);

    const handleUpload = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        files.forEach((file) => {
            validateAndReadFile(file, (result) => {
                onImagesChange((prev) => [...(prev || []), result]);
            });
        });
        if (fileRef.current) fileRef.current.value = '';
    };

    const removeImage = (index) => {
        onImagesChange((prev) => prev.filter((_, i) => i !== index));
    };

    const hasImages = images && images.length > 0;

    return (
        <div>
            <label className="block text-sm font-semibold text-zinc-700 mb-2">{label}</label>
            <ModeToggle mode={mode} setMode={setMode} testIdPrefix={testIdPrefix} />
            {mode === 'text' ? (
                <textarea
                    value={textValue}
                    onChange={(e) => onTextChange(e.target.value)}
                    className="brutalist-textarea"
                    placeholder={placeholder}
                    required={required && !hasImages}
                    style={minHeight ? { minHeight } : undefined}
                    data-testid={`${testIdPrefix}-input`}
                />
            ) : (
                <div className="space-y-3">
                    {hasImages && (
                        <div className="flex flex-wrap gap-3" data-testid={`${testIdPrefix}-image-gallery`}>
                            {images.map((img, i) => (
                                <div key={i} className="relative group">
                                    {img.startsWith('data:application/pdf') ? (
                                        <div className="h-32 w-auto flex items-center gap-2 p-4 rounded-lg border-2 border-zinc-950 bg-zinc-50" data-testid={`${testIdPrefix}-image-preview-${i}`}>
                                            <FileText className="w-6 h-6 text-[#A78BFA]" />
                                            <span className="text-xs font-medium">PDF {i + 1}</span>
                                        </div>
                                    ) : (
                                        <img src={img} alt={`${label} ${i + 1}`} className="h-32 w-auto object-contain rounded-lg border-2 border-zinc-950" data-testid={`${testIdPrefix}-image-preview-${i}`} />
                                    )}
                                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`${testIdPrefix}-remove-image-${i}`}>
                                        <X className="w-3 h-3" />
                                    </button>
                                    <span className="absolute bottom-1 left-1 bg-zinc-950 text-white text-xs px-1.5 py-0.5 rounded">{i + 1}</span>
                                </div>
                            ))}
                            {/* Add more button */}
                            <label className="h-32 w-24 flex flex-col items-center justify-center border-2 border-dashed border-zinc-400 rounded-lg cursor-pointer hover:border-zinc-950 hover:bg-zinc-50 transition-all" data-testid={`${testIdPrefix}-add-more`}>
                                <Plus className="w-6 h-6 text-zinc-400" />
                                <span className="text-zinc-400 text-xs mt-1">Add</span>
                                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple onChange={handleUpload} className="hidden" />
                            </label>
                        </div>
                    )}
                    {!hasImages && (
                        <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-zinc-400 rounded-lg cursor-pointer hover:border-zinc-950 hover:bg-zinc-50 transition-all" data-testid={`${testIdPrefix}-upload-area`}>
                            <Upload className="w-8 h-8 text-zinc-400 mb-2" />
                            <span className="text-zinc-600 font-medium text-sm">Click to upload {label.toLowerCase()}</span>
                            <span className="text-zinc-400 text-xs mt-1">JPEG, PNG, WebP or PDF (max 10MB each) — multiple allowed</span>
                            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple onChange={handleUpload} className="hidden" data-testid={`${testIdPrefix}-file-input`} />
                        </label>
                    )}
                    <NotesField textValue={textValue} onTextChange={onTextChange} label={label} testIdPrefix={testIdPrefix} />
                </div>
            )}
        </div>
    );
}

function ModeToggle({ mode, setMode, testIdPrefix }) {
    return (
        <div className="flex gap-2 mb-3">
            <button type="button" onClick={() => setMode('text')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all text-sm ${mode === 'text' ? 'bg-[#A78BFA] text-white border-zinc-950' : 'bg-white border-zinc-300 hover:border-zinc-500'}`}
                data-testid={`${testIdPrefix}-mode-text`}>
                <Type className="w-4 h-4" /> Type
            </button>
            <button type="button" onClick={() => setMode('image')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all text-sm ${mode === 'image' ? 'bg-[#A78BFA] text-white border-zinc-950' : 'bg-white border-zinc-300 hover:border-zinc-500'}`}
                data-testid={`${testIdPrefix}-mode-image`}>
                <Image className="w-4 h-4" /> Upload Photos
            </button>
        </div>
    );
}

function UploadZone({ fileRef, onUpload, label, testIdPrefix }) {
    return (
        <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-zinc-400 rounded-lg cursor-pointer hover:border-zinc-950 hover:bg-zinc-50 transition-all" data-testid={`${testIdPrefix}-upload-area`}>
            <Upload className="w-8 h-8 text-zinc-400 mb-2" />
            <span className="text-zinc-600 font-medium text-sm">Click to upload {label.toLowerCase()}</span>
            <span className="text-zinc-400 text-xs mt-1">JPEG, PNG, WebP or PDF (max 10MB)</span>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={onUpload} className="hidden" data-testid={`${testIdPrefix}-file-input`} />
        </label>
    );
}

function NotesField({ textValue, onTextChange, label, testIdPrefix }) {
    return (
        <div>
            <label className="block text-xs text-zinc-500 mb-1">Additional notes (optional)</label>
            <textarea value={textValue} onChange={(e) => onTextChange(e.target.value)} className="brutalist-input text-sm" placeholder={`Add any extra context about the ${label.toLowerCase()}...`} rows={2} data-testid={`${testIdPrefix}-notes-input`} />
        </div>
    );
}

export function GradingForm({
    subject, setSubject,
    level, setLevel,
    question, setQuestion,
    studentAnswer, setStudentAnswer,
    answerImages, setAnswerImages,
    questionImage, setQuestionImage,
    loading, error, onSubmit, onClear,
    subjectDropdownOpen, setSubjectDropdownOpen
}) {
    const hasContent = question || studentAnswer || (answerImages?.length > 0) || questionImage;
    const hasAnswer = studentAnswer || (answerImages?.length > 0);
    const hasQuestion = question || questionImage;
    const hasAnyImage = (answerImages?.length > 0) || questionImage;

    const handleClear = () => {
        setAnswerImages([]);
        setQuestionImage(null);
        onClear();
    };

    return (
        <div className="brutalist-card p-8" data-testid="feedback-form-card">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Get AI Feedback</h2>
                {hasContent && (
                    <button onClick={handleClear} className="text-sm text-zinc-500 hover:text-zinc-950 underline" data-testid="clear-form-button">
                        Clear form
                    </button>
                )}
            </div>

            <form onSubmit={onSubmit} className="space-y-6">
                {error && (
                    <div className="p-4 bg-red-50 border-2 border-red-500 rounded-lg" data-testid="feedback-error">
                        <p className="text-red-700 font-medium">{error}</p>
                    </div>
                )}

                <SubjectSelector subject={subject} setSubject={setSubject} dropdownOpen={subjectDropdownOpen} setDropdownOpen={setSubjectDropdownOpen} />

                <LevelSelector level={level} setLevel={setLevel} />

                <SingleImageField
                    label="Question"
                    textValue={question}
                    onTextChange={setQuestion}
                    image={questionImage}
                    onImageChange={setQuestionImage}
                    placeholder="Enter the exam question..."
                    required={true}
                    testIdPrefix="question"
                />

                <MultiImageField
                    label="Your Answer"
                    textValue={studentAnswer}
                    onTextChange={setStudentAnswer}
                    images={answerImages}
                    onImagesChange={setAnswerImages}
                    placeholder="Enter your answer..."
                    required={true}
                    testIdPrefix="answer"
                    minHeight="160px"
                />

                <p className="text-xs text-zinc-500 italic">The AI will automatically match official Edexcel mark schemes to grade your answer.</p>

                <button
                    type="submit"
                    disabled={loading || !hasAnswer || !hasQuestion}
                    className="brutalist-btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="submit-feedback-button"
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            {hasAnyImage ? 'Analyzing your images...' : 'Analyzing your answer...'}
                        </>
                    ) : (
                        <>
                            <Send className="w-5 h-5" />
                            Get AI Feedback
                        </>
                    )}
                </button>
            </form>
        </div>
    );
}

function SubjectSelector({ subject, setSubject, dropdownOpen, setDropdownOpen }) {
    const { subjects } = useSubjects();
    const selectedSubject = subjects.find(s => s.id === subject) || subjects[0];
    const Icon = getSubjectIcon(selectedSubject?.icon);
    return (
        <div>
            <label className="block text-sm font-semibold text-zinc-700 mb-2">Subject</label>
            <div className="relative">
                <button type="button" onClick={() => setDropdownOpen(!dropdownOpen)} className="brutalist-input flex items-center justify-between w-full" data-testid="subject-selector">
                    <span className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-[#7C3AED]" />
                        <span>{selectedSubject?.name || 'Select subject'}</span>
                    </span>
                    <ChevronDown className={`w-5 h-5 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {dropdownOpen && (
                    <div className="absolute z-10 w-full mt-2 bg-white border-2 border-zinc-950 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                        {subjects.map((s) => {
                            const IconEl = getSubjectIcon(s.icon);
                            return (
                                <button key={s.id} type="button" onClick={() => { setSubject(s.id); setDropdownOpen(false); }}
                                    className={`w-full text-left px-4 py-3 flex items-center gap-2 hover:bg-zinc-50 ${subject === s.id ? 'bg-[#EDE9FE]' : ''}`}
                                    data-testid={`subject-option-${s.id}`}>
                                    <IconEl className="w-4 h-4 text-[#7C3AED]" />
                                    <span className="font-medium">{s.name}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function LevelSelector({ level, setLevel }) {
    const active = level || 'IAL';
    return (
        <div>
            <label className="block text-sm font-semibold text-zinc-700 mb-2">Level</label>
            <div className="inline-flex bg-zinc-100 p-1 rounded-full" data-testid="level-selector">
                {LEVELS.map((l) => (
                    <button
                        key={l.id}
                        type="button"
                        onClick={() => setLevel?.(l.id)}
                        className={`px-5 py-2 text-sm font-bold rounded-full transition-all ${
                            active === l.id ? 'bg-white text-[#7C3AED] shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                        }`}
                        data-testid={`level-option-${l.id}`}
                    >
                        {l.name}
                    </button>
                ))}
            </div>
        </div>
    );
}

export { SUBJECTS };
